/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useEffect, useState } from "react";
import { useTraslados } from "../../../TrasladosContext";
import api from "../../../axiosConfig";
import { Button, message, DatePicker, Tooltip } from "antd";
import { LeftOutlined, RightOutlined, CalendarOutlined } from "@ant-design/icons";
import {
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  UnifiedContainer,
  UnifiedLogo,
  UnifiedCardLight,
  UnifiedTitle,
  CenteredLayout,
} from "../../../theme/SharedDesign";
import {
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import dayjs from "../../../utils/dayjs";
import { useNavigate } from "react-router-dom";
import logo from "../../../Assets/img/logo.webp";
import locale from "antd/es/date-picker/locale/es_ES";

import TrasladoList from "./TrasladoList";
import { useUser } from "../../../UserContext";
import { esAdministrador, rutaTraslados } from "../../../utils/perfil";
import { styled } from '@mui/material/styles';

// Navegación de fecha con estilo claro
const DateNavigation = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(20px)',
  borderRadius: 16,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  border: '2px solid rgba(255, 255, 255, 0.3)',
  boxShadow: `
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.8)
  `,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  position: 'relative',
  zIndex: 10,
  
  '& .ant-picker': {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderColor: 'rgba(10, 46, 92, 0.3)',
    borderRadius: 12,
    height: 45,
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    minWidth: 180,
    
    '&:hover, &.ant-picker-focused': {
      borderColor: 'rgba(10, 46, 92, 0.5)',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      boxShadow: '0 0 20px rgba(10, 46, 92, 0.3)',
    },
    
    '& .ant-picker-input > input': {
      color: '#2c3e50 !important',
      fontWeight: 500,
      fontSize: '0.95rem',
      textAlign: 'center',
    },
    
    '& .ant-picker-suffix': {
      color: 'rgba(10, 46, 92, 0.8)',
    },
  },
  
  '& .ant-btn': {
    backgroundColor: 'rgba(10, 46, 92, 0.1)',
    borderColor: 'rgba(10, 46, 92, 0.3)',
    color: '#0a2e5c',
    height: 45,
    width: 45,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(10px)',
    
    '&:hover': {
      backgroundColor: 'rgba(10, 46, 92, 0.2)',
      borderColor: 'rgba(10, 46, 92, 0.5)',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 20px rgba(10, 46, 92, 0.3)',
      color: '#051b38',
    },
  },
  
  '& input[type="date"]': {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    border: '1px solid rgba(10, 46, 92, 0.3)',
    borderRadius: 12,
    height: 45,
    padding: '0 16px',
    fontSize: '0.95rem',
    color: '#2c3e50',
    textAlign: 'center',
    minWidth: 180,
    backdropFilter: 'blur(10px)',
    
    '&::-webkit-calendar-picker-indicator': {
      filter: 'invert(0.2)',
      cursor: 'pointer',
    },
    
    '&:hover, &:focus': {
      borderColor: 'rgba(10, 46, 92, 0.5)',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      boxShadow: '0 0 20px rgba(10, 46, 92, 0.3)',
    },
  },
}));



function Dashboard() {
  const { traslados, setTrasladosMerge, updateTraslado } = useTraslados();
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useUser();
  // Administrador de vehículos: traslados de todos sus vehículos, solo lectura
  const administrador = esAdministrador(user);

  // Se carga la información de traslados cada vez que cambia la fecha o el perfil
  useEffect(() => {
    fetchTraslados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, user?.id, user?.role]);


  // Función para obtener los traslados según la fecha seleccionada
  const fetchTraslados = async () => {
    setLoading(true);
    try {
      const response = await api.get(rutaTraslados(user), {
        params: {
          fecha: selectedDate.format("YYYY-MM-DD"),
        },
      });
      setTrasladosMerge(response.data);
    } catch (error) {
      console.error("Error al cargar los traslados:", error);
      message.error("Error al cargar los traslados");
    } finally {
      setLoading(false);
    }
  };

  // Cambiar la fecha hacia el día anterior
  const handlePrevDay = () => {
    setSelectedDate((prevDate) => prevDate.subtract(1, "day"));
  };

  // Cambiar la fecha hacia el siguiente día
  const handleNextDay = () => {
    setSelectedDate((prevDate) => prevDate.add(1, "day"));
  };

  // Al hacer clic en un traslado se actualiza su estado y se navega a la vista detalle
  const handleTrasladoClick = async (traslado) => {
    // "Enterado" lo marca solo el conductor del traslado
    if (traslado.status === "Asignado" && !administrador) {
      try {
        await api.put(`/traslados/enterado/${traslado.id}`);
        updateTraslado({ ...traslado, status: "Enterado" });
      } catch (error) {
        console.error("Error al actualizar el traslado:", error);
        message.error("Error al actualizar el traslado");
      }
    }
    navigate(`/modules/dashboard/traslado/${traslado.id}`);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      Cancelado: "#b10e1a",
      Finalizado: "#4d4d4d",
      Fallido: "#4a2c04",
      FallidoXvalidar: "#d4380d",
      Asignado: "#1f7362",
      "Pre-Asignado": "#0c27a0",
      Programado: "#6c06b5",
      Enterado: "#0c5b07",
      Iniciado: "#fa7114",
      Aceptado: "#088A08",
      "En Camino": "#FFBF00",
      Esperando: "#a44706",
      default: "#b0b500",
    };
    return statusColors[status] || statusColors.default;
  };

  // Filtrar los traslados para mostrar solo los correspondientes al día seleccionado
  const filteredTraslados = traslados.filter((traslado) =>
    dayjs(traslado.fecha).isSame(selectedDate, "day")
  );


  return (
    <UnifiedContainer>
      <CenteredLayout isMobile={isMobile} maxWidth={600}>
        <UnifiedLogo src={logo} alt="Mogotax Logo" isMobile={isMobile} />
        
        <UnifiedCardLight sx={{ width: '100%', position: 'relative', zIndex: 1 }} isMobile={isMobile}>
          <Box sx={{ px: isMobile ? 2 : 0 }}>
            <UnifiedTitle variant="h4" isMobile={isMobile}>
              <DashboardIcon sx={{ fontSize: isMobile ? '1.6rem' : '2rem', color: '#0a2e5c' }} />
              <span style={{ color: '#0a2e5c' }}>
                {administrador ? "Traslados de mis vehículos" : "Mis Traslados"}
              </span>
            </UnifiedTitle>

            {/* Navegación de fecha */}
            <DateNavigation>
            <Tooltip title="Día anterior">
              <Button
                icon={<LeftOutlined />}
                onClick={handlePrevDay}
                shape="circle"
              />
            </Tooltip>
            
            {isMobile ? (
              // Para móviles, usamos el input nativo de tipo date
              <input
                type="date"
                value={selectedDate.format("YYYY-MM-DD")}
                onChange={(e) => setSelectedDate(dayjs(e.target.value))}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(10, 46, 92, 0.3)',
                  borderRadius: 12,
                  height: 45,
                  padding: '0 16px',
                  fontSize: '0.95rem',
                  color: '#0a2e5c',
                  textAlign: 'center',
                  minWidth: 180,
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                }}
              />
            ) : (
              // En escritorio, usamos el DatePicker de Ant Design
              <DatePicker
                value={selectedDate}
                onChange={(date) => date && setSelectedDate(date)}
                format="DD/MM/YYYY"
                locale={locale}
                allowClear={false}
                suffixIcon={<CalendarOutlined />}
                placeholder="Seleccionar fecha"
              />
            )}
            
            <Tooltip title="Día siguiente">
              <Button
                icon={<RightOutlined />}
                onClick={handleNextDay}
                shape="circle"
              />
            </Tooltip>
          </DateNavigation>
          </Box>

          {/* Lista de traslados filtrados */}
          <Box sx={{ position: 'relative', zIndex: 10, width: '100%' }}>
            <TrasladoList
              traslados={filteredTraslados}
              loading={loading}
              onSelect={handleTrasladoClick}
              getStatusColor={getStatusColor}
            />
          </Box>
        </UnifiedCardLight>
      </CenteredLayout>
    </UnifiedContainer>
  );
}

export default Dashboard;
